/**
 * Explore Page - Trending posts, hashtags, and search
 */

import { useState, useEffect } from 'react'
import { Search, TrendingUp, Hash, Users, MapPin, Clock, X, Image as ImageIcon } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import Post from '../components/Post'
import Avatar from '../components/ui/Avatar'
import api from '../services/api'

interface TrendingHashtag {
  tag: string
  count: number
}

interface UserResult {
  userId: string
  username: string
  fullName: string
  avatar: string
  bio: string
  verified: boolean
  isFollowing: boolean
}

interface LocationResult {
  city: string
  state: string
  country: string
  postCount: number
  thumbnailUrl: string
}

type SearchTab = 'top' | 'latest' | 'people' | 'media'

export default function Explore() {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<'trending' | 'latest'>('trending')
  const [searchTab, setSearchTab] = useState<SearchTab>('top')
  const [posts, setPosts] = useState<any[]>([])
  const [users, setUsers] = useState<UserResult[]>([])
  const [locations, setLocations] = useState<LocationResult[]>([])
  const [trendingHashtags, setTrendingHashtags] = useState<TrendingHashtag[]>([])
  const [recentSearches, setRecentSearches] = useState<string[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchExplorePosts()
    fetchTrendingHashtags()
    loadRecentSearches()
  }, [activeTab])

  const loadRecentSearches = () => {
    const saved = localStorage.getItem('recent_searches')
    if (saved) {
      setRecentSearches(JSON.parse(saved))
    }
  }

  const saveRecentSearch = (query: string) => {
    if (!query.trim()) return

    const updated = [query, ...recentSearches.filter(s => s !== query)].slice(0, 10)
    setRecentSearches(updated)
    localStorage.setItem('recent_searches', JSON.stringify(updated))
  }

  const clearRecentSearches = () => {
    setRecentSearches([])
    localStorage.removeItem('recent_searches')
  }

  const removeRecentSearch = (query: string) => {
    const updated = recentSearches.filter(s => s !== query)
    setRecentSearches(updated)
    localStorage.setItem('recent_searches', JSON.stringify(updated))
  }

  const fetchExplorePosts = async () => {
    try {
      setLoading(true)

      const result = await api.getExploreFeed({
        type: activeTab,
        limit: 20
      })

      if (result.success && result.posts.length > 0) {
        const normalizedPosts = result.posts.map(post => ({
          id: post.postId,
          author: post.author,
          content: post.content,
          mediaUrl: post.mediaUri,
          mediaType: post.mediaType,
          thumbnailUrl: post.thumbnailUri,
          createdAt: post.createdAt,
          location: post.location ? {
            address: `${post.location.city}, ${post.location.country}`
          } : undefined,
          onRecordCode: post.onRecordCode,
          stats: post.stats
        }))

        setPosts(normalizedPosts)
      }
    } catch (error) {
      console.error('Error fetching explore posts:', error)
      setPosts([])
    } finally {
      setLoading(false)
    }
  }

  const fetchTrendingHashtags = async () => {
    try {
      const result = await api.getTrendingHashtags({ limit: 5 })

      if (result.success && result.hashtags.length > 0) {
        setTrendingHashtags(result.hashtags)
      }
    } catch (error) {
      console.error('Error fetching trending hashtags:', error)
      // Fallback handled by API method (mock data)
    }
  }

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!searchQuery.trim()) {
      // If search is cleared, reload explore posts
      setIsSearching(false)
      fetchExplorePosts()
      return
    }

    setIsSearching(true)
    saveRecentSearch(searchQuery)

    // Search based on active tab
    switch (searchTab) {
      case 'top':
        // Search both posts and users
        await Promise.all([searchPosts(), searchUsers()])
        break
      case 'latest':
        await searchPosts()
        break
      case 'people':
        await searchUsers()
        break
      case 'media':
        await searchPosts() // will filter to media-only in render
        break
    }
  }

  const searchPosts = async () => {
    try {
      setLoading(true)

      const result = await api.searchPosts({
        query: searchQuery,
        limit: 20
      })

      if (result.success && result.posts.length > 0) {
        const normalizedPosts = result.posts.map(post => ({
          id: post.postId,
          author: post.author,
          content: post.content,
          mediaUrl: post.mediaUri,
          mediaType: post.mediaType,
          thumbnailUrl: post.thumbnailUri,
          createdAt: post.createdAt,
          location: post.location ? {
            address: `${post.location.city}, ${post.location.country}`
          } : undefined,
          onRecordCode: post.onRecordCode,
          stats: post.stats
        }))

        setPosts(normalizedPosts)
      } else {
        setPosts([])
      }
    } catch (error) {
      console.error('Error searching posts:', error)
      setPosts([])
    } finally {
      setLoading(false)
    }
  }

  const searchUsers = async () => {
    try {
      setLoading(true)

      const result = await api.searchUsers({
        query: searchQuery,
        limit: 20
      })

      if (result.success && result.users) {
        setUsers(result.users)
      } else {
        setUsers([])
      }
    } catch (error) {
      console.error('Error searching users:', error)
      setUsers([])
    } finally {
      setLoading(false)
    }
  }

  const searchLocations = async () => {
    try {
      setLoading(true)

      const result = await api.searchLocations({
        query: searchQuery,
        limit: 20
      })

      if (result.success && result.locations) {
        setLocations(result.locations)
      } else {
        setLocations([])
      }
    } catch (error) {
      console.error('Error searching locations:', error)
      setLocations([])
    } finally {
      setLoading(false)
    }
  }

  const formatCount = (count: number) => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`
    } else if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`
    }
    return count.toString()
  }

  return (
    <div>
      {/* Header */}
      <div className="sticky top-0 bg-white bg-opacity-90 backdrop-blur-sm border-b border-gray-200 z-10">
        <div className="px-4 py-3">
          <h1 className="text-xl font-bold mb-3">Explore</h1>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search posts, users, or locations..."
              className="w-full pl-10 pr-10 py-2 bg-gray-100 rounded-full outline-none focus:bg-gray-200 transition-colors"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => {
                  setSearchQuery('')
                  setIsSearching(false)
                  fetchExplorePosts()
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </form>
        </div>

        {/* Search Tabs — Twitter-style: Top, Latest, People, Media */}
        {isSearching && (
          <div className="flex border-b border-gray-200">
            {([
              { key: 'top', label: 'Top' },
              { key: 'latest', label: 'Latest' },
              { key: 'people', label: 'People' },
              { key: 'media', label: 'Media' },
            ] as const).map(({ key, label }) => (
              <button key={key}
                onClick={() => {
                  setSearchTab(key)
                  if (key === 'top') { searchPosts(); searchUsers() }
                  else if (key === 'latest') searchPosts()
                  else if (key === 'people') searchUsers()
                  else if (key === 'media') searchPosts()
                }}
                className={`flex-1 py-3 text-center text-sm font-semibold transition-colors ${
                  searchTab === key
                    ? 'text-black border-b-4 border-onrecord-orange'
                    : 'text-gray-500 hover:bg-gray-50'
                }`}>
                {label}
              </button>
            ))}
          </div>
        )}

        {/* Explore Tabs (shown when not searching) */}
        {!isSearching && (
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('trending')}
              className={`flex-1 py-3 text-center font-semibold transition-colors ${
                activeTab === 'trending'
                  ? 'text-black border-b-4 border-onrecord-orange'
                  : 'text-gray-500 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Trending
              </div>
            </button>
            <button
              onClick={() => setActiveTab('latest')}
              className={`flex-1 py-3 text-center font-semibold transition-colors ${
                activeTab === 'latest'
                  ? 'text-black border-b-4 border-onrecord-orange'
                  : 'text-gray-500 hover:bg-gray-50'
              }`}
            >
              Latest
            </button>
          </div>
        )}
      </div>

      {/* Recent Searches (shown when search box has focus and no query) */}
      {!isSearching && !searchQuery && recentSearches.length > 0 && (
        <div className="border-b border-gray-200 bg-white p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold text-sm text-gray-700 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Recent Searches
            </h2>
            <button
              onClick={clearRecentSearches}
              className="text-sm text-onrecord-orange hover:text-onrecord-orange-dark font-medium"
            >
              Clear All
            </button>
          </div>
          <div className="space-y-2">
            {recentSearches.slice(0, 5).map((recent, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg cursor-pointer"
                onClick={() => {
                  setSearchQuery(recent)
                  setIsSearching(true)
                }}
              >
                <div className="flex items-center gap-3">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-700">{recent}</span>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    removeRecentSearch(recent)
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Trending Hashtags Section */}
      {activeTab === 'trending' && trendingHashtags.length > 0 && (
        <div className="border-b border-gray-200 bg-gray-50 p-4">
          <h2 className="font-bold text-lg mb-3 flex items-center gap-2">
            <Hash className="w-5 h-5 text-onrecord-orange" />
            Trending Hashtags
          </h2>
          <div className="space-y-2">
            {trendingHashtags.map((hashtag, index) => (
              <button
                key={hashtag.tag}
                onClick={() => {
                  setSearchQuery(`#${hashtag.tag}`)
                  handleSearch(new Event('submit') as any)
                }}
                className="w-full text-left p-3 bg-white rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">#{index + 1}</span>
                      <span className="font-bold text-onrecord-orange">#{hashtag.tag}</span>
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      {formatCount(hashtag.count)} posts
                    </div>
                  </div>
                  <TrendingUp className="w-5 h-5 text-gray-400" />
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Results — Top tab shows users then posts mixed */}
      {isSearching && searchTab === 'top' && (
        loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-onrecord-orange"></div>
          </div>
        ) : (posts.length === 0 && users.length === 0) ? (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Search className="w-8 h-8 text-gray-400" />
            </div>
            <h2 className="text-2xl font-bold mb-2">No results</h2>
            <p className="text-gray-500 text-center">Try a different search term or OnRecord code</p>
          </div>
        ) : (
          <div>
            {/* Users first (top 3) */}
            {users.length > 0 && (
              <div className="border-b border-gray-200">
                <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase">People</div>
                {users.slice(0, 3).map((user) => (
                  <div key={user.userId} onClick={() => navigate(`/profile/${user.userId}`)}
                    className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 cursor-pointer">
                    <div className="flex items-center gap-3">
                      <Avatar src={user.avatar} alt={user.fullName} size="md" />
                      <div>
                        <p className="font-semibold text-gray-900 text-sm">{user.fullName}</p>
                        <p className="text-sm text-gray-500">@{user.username}</p>
                      </div>
                    </div>
                  </div>
                ))}
                {users.length > 3 && (
                  <button onClick={() => setSearchTab('people')} className="px-4 py-2 text-sm text-onrecord-orange hover:underline">
                    View all {users.length} people
                  </button>
                )}
              </div>
            )}
            {/* Posts */}
            {posts.map((post) => (
              <Post key={post.id} post={post} />
            ))}
          </div>
        )
      )}

      {/* Latest tab — posts by newest */}
      {(!isSearching || searchTab === 'latest') && (
        loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-onrecord-orange"></div>
          </div>
        ) : posts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Search className="w-8 h-8 text-gray-400" />
            </div>
            <h2 className="text-2xl font-bold mb-2">No results found</h2>
            <p className="text-gray-500 text-center max-w-md">
              {searchQuery ? 'Try a different search term or OnRecord code' : 'Posts will appear here'}
            </p>
          </div>
        ) : (
          <div>
            {posts.map((post) => (
              <Post key={post.id} post={post} />
            ))}
          </div>
        )
      )}

      {/* Media tab — posts with media only */}
      {isSearching && searchTab === 'media' && (
        loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-onrecord-orange"></div>
          </div>
        ) : posts.filter(p => p.mediaUrl).length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <ImageIcon className="w-8 h-8 text-gray-400" />
            </div>
            <h2 className="text-2xl font-bold mb-2">No media found</h2>
            <p className="text-gray-500">No photos or videos match your search</p>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-0.5 p-1">
            {posts.filter(p => p.mediaUrl).map((post) => (
              <div key={post.id} onClick={() => navigate(`/post/${post.id}`)}
                className="aspect-square cursor-pointer overflow-hidden hover:opacity-90 transition-opacity">
                {post.mediaType === 'video' ? (
                  <video src={post.mediaUrl} className="w-full h-full object-cover" muted />
                ) : (
                  <img src={post.mediaUrl} alt="" className="w-full h-full object-cover" />
                )}
              </div>
            ))}
          </div>
        )
      )}

      {/* People tab */}
      {isSearching && searchTab === 'people' && (
        loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-onrecord-orange"></div>
          </div>
        ) : users.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Users className="w-8 h-8 text-gray-400" />
            </div>
            <h2 className="text-2xl font-bold mb-2">No users found</h2>
            <p className="text-gray-500 text-center max-w-md">
              Try a different search term
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {users.map((user) => (
              <div
                key={user.userId}
                onClick={() => navigate(`/profile/${user.userId}`)}
                className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Avatar
                      src={user.avatar}
                      alt={user.fullName}
                      size="md"
                    />
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-gray-900">{user.fullName}</p>
                        {user.verified && (
                          <div className="w-4 h-4 bg-onrecord-orange rounded-full flex items-center justify-center">
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">@{user.username}</p>
                      {user.bio && (
                        <p className="text-sm text-gray-600 mt-1 line-clamp-1">{user.bio}</p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={async (e) => {
                      e.stopPropagation()
                      const prev = user.isFollowing
                      // Optimistic update
                      setUsers(us => us.map(u =>
                        u.userId === user.userId ? { ...u, isFollowing: !prev } : u
                      ))
                      try {
                        if (prev) {
                          await api.unfollowUser(user.userId)
                        } else {
                          await api.followUser(user.userId)
                        }
                      } catch {
                        // Revert on error
                        setUsers(us => us.map(u =>
                          u.userId === user.userId ? { ...u, isFollowing: prev } : u
                        ))
                      }
                    }}
                    className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
                      user.isFollowing
                        ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        : 'bg-onrecord-orange text-white hover:bg-onrecord-orange-dark'
                    }`}
                  >
                    {user.isFollowing ? 'Following' : 'Follow'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* Locations Results */}
    </div>
  )
}
